import mapKnownValues from "@/utils/mapKnownValues";
import Cluster from "@/classes/cluster";
import { ItemStatusType } from "@/classes/itemStatusType";
import StationCreditProgram from "@/classes/stationCreditProgram";

export const stationMakeToModel = {
  EvoCharge: ["EVC3AB0A2E1A1", "EVC3AB0B2E1A1", "EVC3AC0A1E1A1", "EVC3AC0B1E1A1"],
  Tritium: ["TRI153-RTM-01"]
};

export const fundingTypeValues = [
  { key: "FOURPACK", name: "4 Pack" },
  { key: "FREE", name: "Free" },
  { key: "CASH", name: "Cash" },
  { key: "GSA", name: "GSA" },
  { key: "VW", name: "VW Mitigation Trust Grant" },
  { key: "LADWP", name: "LADWP Rebate" },
  { key: "SCE_CSMR", name: "SCE CSMR Rebate" },
  { key: "SCE_MUDDAC", name: "SCE MUDDAC Rebate" },
  { key: "SCE_TURNKEY", name: "SCE Turnkey Rebate" },
  { key: "SCE_NC", name: "SCE NC Rebate" },
  { key: "NVE", name: "NVE Rebate" },
  { key: "CALeVIP", name: "CALeVIP Rebate" },
  { key: "TEP", name: "TEP Rebate" },
  { key: "SDGE", name: "SDGE Rebate" },
  { key: "SJV", name: "SJV Rebate" },
  { key: "SRP", name: "SRP Rebate" },
  { key: "SMUD", name: "SMUD Rebate" },
  { key: "RMP", name: "RMP Rebate" },
  { key: "APU", name: "APU Rebate" },
  { key: "PCE", name: "PCE Rebate" },
  { key: "SVCE", name: "SVCE Rebate" },
  { key: "JUNY", name: "JUNY Rebate" }
];

export const stationMakes = Object.keys(stationMakeToModel);

export enum StationStatus {
  UNREGISTERED = "UNREGISTERED",
  UNAVAILABLE = "UNAVAILABLE",
  DISCONNECTED = "DISCONNECTED", // TODO: Backend should support this
  AVAILABLE = "AVAILABLE",
  PREPARING = "PREPARING",
  CHARGING = "CHARGING",
  FINISHING = "FINISHING",
  NEEDS_SERVICE = "NEEDS_SERVICE", // Combination one I use for pages
  RESERVING = "RESERVING",
  SUSPENDEDEVSE = "SUSPENDEDEVSE",
  SUSPENDEDEV = "SUSPENDEDEV",
  FAULTED = "FAULTED",
  UNHEALTHY = "UNHEALTHY"
}

export const stationStatusValues = Object.values(StationStatus).sort();
export const stationLevelValues = [2, 3];

export const stationTypeValues = ["PUBLIC", "SHARED", "PRIVATE"];

export type StationAPIResponse = {
  id: string;
  organizationId: string;
  propertyId: string;
  clusterId: string;
  status: StationStatus;
  serialNo: string;
  qrCode: string | null;
  floor: string;
  parkSpace: string;
  make: string;
  model: string;
  stationLevel: number | string | null;
  firmwareVer: string;
  installed: string; // date
  exclusive: number;
  createdTime: string; // date
  registeredTime: string | null; // date
  itemStatus: ItemStatusType;
  exclusiveUsr1: string | null;
  exclusiveUsr2: string | null;
  isSubscription: boolean;
  isFree: boolean;
  isGateway: boolean;
  warrantyDate: string | null; // date
  note: string;
  macAddress: string;
  productionStatus: string;
  fundingType: string;
  stationType: string;
  latitude: string;
  longitude: string;
};

/**
 * Station Details
 */
export default class Station {
  id = "";
  clusterId = ""; // Cluster to which the station belongs to
  createdTime = new Date();
  connector: any = [];
  connectorCount = 1;
  stationLevel: number | string | null = null;
  isFree = false;
  isSubscription = false;
  isGateway = false;
  itemStatus: ItemStatusType = ItemStatusType.DISABLED;
  organizationId = "";
  propertyId = "";
  registeredTime = new Date();
  exclusive = 0;
  exclusiveUsr1: string | null = null;
  exclusiveUsr2: string | null = null;
  firmwareVer = "";
  floor = "";
  installed = new Date();
  make = "";
  model = "";
  type = "";
  parkSpace = "";
  qrCode: string | null = null;
  serialNo = "";
  status: StationStatus = StationStatus.UNAVAILABLE;
  warrantyDate: Date | null = new Date();
  note = "";
  macAddress = "";
  productionStatus: null | string = null;
  fundingType: null | string = null;
  stationType: null | string = null;
  latitude: null | string = null;
  longitude: null | string = null;

  // Extra concepts
  _originalCreditPrograms: StationCreditProgram[] = [];
  creditPrograms: StationCreditProgram[] = [];
  cluster = new Cluster();
  _isDisconnected = false; // TODO: UI ONLY, should eventually make part of the station
  _isUnhealthy = false; // TODO: UI ONLY, should eventually make part of the station

  constructor(params: Partial<Station> = {}) {
    mapKnownValues(this, params);
    this.cluster = new Cluster(params.cluster);
    this._originalCreditPrograms = JSON.parse(JSON.stringify(this.creditPrograms.slice(0)));
  }

  get hasChangedCreditPrograms() {
    return JSON.stringify(this._originalCreditPrograms) !== JSON.stringify(this.creditPrograms);
  }

  get stationName() {
    if (this.qrCode) return this.qrCode;
    return this.serialNo;
  }

  get canStartStation() {
    return (
      this.itemStatus === ItemStatusType.ACTIVE &&
      (this.status === StationStatus.PREPARING || this.status === StationStatus.AVAILABLE)
    );
  }

  get exclusiveUsers() {
    const users: string[] = [];
    if (this.exclusiveUsr1) users.push(this.exclusiveUsr1);
    if (this.exclusiveUsr2) users.push(this.exclusiveUsr2);
    return users;
  }

  get needsService() {
    return this.status === StationStatus.FAULTED || this._isDisconnected;
  }

  get computedStatus() {
    if (this._isUnhealthy) return "UNHEALTHY";
    if (this._isDisconnected) return "DISCONNECTED";
    return this.status;
  }

  addExclusiveUser(id: string) {
    if (this.exclusiveUsr1 === null) {
      this.exclusiveUsr1 = id;
      return;
    }
    if (this.exclusiveUsr2 === null) {
      this.exclusiveUsr2 = id;
    }
  }

  addCreditProgram() {
    this.creditPrograms.push(
      new StationCreditProgram({
        stationId: this.id
      })
    );
  }

  removeCreditProgram(creditProgram: StationCreditProgram) {
    const creditProgramIndexFound = this.creditPrograms.findIndex(p => p === creditProgram);
    if (creditProgramIndexFound === -1) return;
    this.creditPrograms.splice(creditProgramIndexFound, 1);
  }

  removeExclusiveUser(email: string) {
    if (this.exclusiveUsr1 === email) this.exclusiveUsr1 = null;
    else if (this.exclusiveUsr2 === email) this.exclusiveUsr2 = null;
  }

  removeAllExclusiveUsers() {
    this.exclusiveUsr1 = null;
    this.exclusiveUsr2 = null;
  }

  toJson() {
    return {
      ...this,
      _originalCreditPrograms: undefined,
      creditPrograms: undefined,
      _isDisconnected: undefined,
      cluster: undefined
    };
  }

  static fromAPI(
    response: StationAPIResponse,
    cluster: Cluster | undefined = undefined,
    isDisconnected = false,
    creditPrograms: StationCreditProgram[] = []
  ) {
    return new Station({
      ...response,
      cluster,
      creditPrograms,
      _isDisconnected: isDisconnected,
      createdTime: response.createdTime ? new Date(response.createdTime) : new Date(),
      registeredTime: response.registeredTime ? new Date(response.registeredTime) : new Date(),
      installed: response.installed ? new Date(response.installed) : new Date(),
      warrantyDate: response.warrantyDate ? new Date(response.warrantyDate) : null
    });
  }
}
